import { ToolCallSummary } from "./call";
import { EmailDirection, EmailMessageStatus } from "./email";
import { CallStatus } from "./enums/call";
import { ChatMode, ChatStatus } from "./enums/chat";
import { MessageSummary } from "./message";
import { UserTiny } from "./user";

export interface ChatEmailSummary {
  id: string;
  threadId: string;
  subject: string | null;
  snippet: string | null;
  direction: EmailDirection;
  status: EmailMessageStatus;
  sentDate: string | null;
  createdDate: string;
}

export interface ChatPatient {
  id: string;
  firstName: string;
  lastName: string;
  primaryLocationId?: string;
  profilePictureUrl?: string;
  isArchived?: boolean;
}

export interface ChatBase {
  id: string;
  status: ChatStatus;
  // Portal-only patients have no phone; their chat is keyed by primaryPatientId.
  phoneNumber: string | null;
  primaryPatientId: string;
  leadId?: string | null;
  patients: ChatPatient[];
  patientOptedOutOfSms?: boolean;
  clinicOptedOutOfSms?: boolean;
  isLandlineNumber?: boolean;
  mode?: ChatMode;
}

export function isOptedOutOfSms(
  chat: Pick<ChatBase, "patientOptedOutOfSms" | "clinicOptedOutOfSms">,
): boolean {
  return !!chat.patientOptedOutOfSms || !!chat.clinicOptedOutOfSms;
}

export function isLandlineNumber(
  chat: Pick<ChatBase, "isLandlineNumber">,
): boolean {
  return !!chat.isLandlineNumber;
}

export function isSmsBlocked(
  chat: Pick<
    ChatBase,
    "patientOptedOutOfSms" | "clinicOptedOutOfSms" | "isLandlineNumber"
  >,
): boolean {
  return isOptedOutOfSms(chat) || isLandlineNumber(chat);
}

export interface ChatSummary extends ChatBase {
  latestMessageContent: string;
  latestMessageDate: string;
}

export interface ChatDetail extends ChatBase {
  messages: (CallDetail | MessageSummary)[];
  emails: ChatEmailSummary[];
  createdDate: string;
  updatedDate: string;
}

export interface ChatSnapshot {
  id: string;
  status?: ChatStatus;
  primaryPatientId?: string;
  leadId?: string | null;
  patients?: ChatPatient[];
  latestMessageContent?: string;
  latestMessageDate?: string;
  createdDate?: string;
  patientOptedOutOfSms?: boolean;
  clinicOptedOutOfSms?: boolean;
  isLandlineNumber?: boolean;
  mode?: ChatMode;
}

export interface UnreadCounts {
  total: number;
  // location_id -> count, only for locations whose count changed. Absent when
  // the backend is older than the server-push change (fall back to refetch).
  byLocation: Record<string, number>;
}

export interface ChatUpdate {
  id: string;
  updatedFrom: ChatSnapshot;
  updatedTo: ChatSnapshot;
  // Present only on a real READ<->UNREAD flip from a count-pushing backend.
  unreadCounts?: UnreadCounts;
}

export interface ChatTaskDraft {
  title: string;
  description: string;
}

export type SuggestedMessage = {
  id: string;
  chatId: string;
  fromMessageId: string;
  content: string;
  assistantId: string;
  isSelected: boolean;
  isArchived?: boolean;
  createdDate: string;
};

export type CallDetail = {
  id: string;
  // ``sender`` is null for shape-(c) outbound calls placed from a shared
  // desk phone with no owning user — attribution comes through
  // ``originatingDeskPhone`` instead.
  sender: UserTiny | null;
  chatId: string;
  direction: "INBOUND" | "OUTBOUND";
  status: CallStatus;
  createdDate: string;
  completedDate: string;
  summary: string;
  pathToAudio: string;
  context: { [key: string]: any };
  retellCallId: string | null;
  toolCalls: ToolCallSummary[];
  type: "AI" | "MANUAL";
  originatingDeskPhone?: { id: string; displayName: string } | null;
  answeredViaDeskPhone?: { id: string; displayName: string } | null;
};

export type CallCreate = {
  patientId: string;
  senderId: string;
};

export type MessageCreate = {
  chatId: string;
  messageUid: string;
  content: string;
  senderId: string;
};

export interface ChatFilters {
  unreadOnly: boolean;
  unrespondedOnly: boolean;
  locationId?: string;
  humanOnly: boolean;
  searchTerm: string;
}

export type ChatWindowState = {
  chatId?: string;
  isMinimized: boolean;
  // Stable id for placeholder windows so the store can match them across
  // spread-based updates (the chatId stays "" until the API resolves).
  localId?: string;
};
